import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
} from '@chakra-ui/react';
import {
  FiPhoneCall,
  FiMessageSquare,
  FiClock,
  FiCheckCircle,
  FiActivity,
  FiInbox,
  FiAlertCircle,
  FiRefreshCw,
  FiSmile,
  FiStar,
  FiAlertTriangle,
  FiMessageCircle,
} from 'react-icons/fi';
import { 
  MdOutlineMarkEmailRead, 
  MdOutlineChat,
  MdHeadsetMic,
  MdOutlineAutoAwesome,
  MdSpeed,
} from 'react-icons/md';

// Import modular components
import {
  StatCard,
  DashboardHeader,
  TicketsRecentes,
  CategoriasTickets,
  EquipeStatus,
  FaqPopulares,
  CanaisAtendimento,
  TrendChart
} from './components';

// Import services and utilities
import { fetchDashboardData } from './services/dashboardService';
import { formatNumber, formatTime, formatPercentage } from './utils/formatters';

/**
 * SuporteDashboard - Main dashboard component for support users
 * Provides metrics and tools for monitoring customer support performance
 */
const SuporteDashboard = () => {
  // Initialize with safe default values
  const [stats, setStats] = useState({
    ticketsAbertos: { value: 0, change: 0 },
    ticketsResolvidos: { value: 0, change: 0 },
    tempoMedioResposta: { value: 0, change: 0 },
    tempoMedioResolucao: { value: 0, change: 0 },
    satisfacaoCliente: { value: 0, change: 0 },
    ticketsReabertos: { value: 0, change: 0 },
    contatosPorDia: { value: 0, change: 0 },
    atendimentosPrioritarios: { value: 0, change: 0 },
  });
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [ticketsRecentes, setTicketsRecentes] = useState([]);
  const [categoriasTickets, setCategoriasTickets] = useState([]);
  const [equipeStatus, setEquipeStatus] = useState([]);
  const [faqPopulares, setFaqPopulares] = useState([]);
  const [canaisAtendimento, setCanaisAtendimento] = useState([]);
  const [trend, setTrend] = useState([]);
  const [error, setError] = useState(null);
  const toast = useToast();

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch data from API
      const data = await fetchDashboardData(timeFrame);
      
      // Check if data contains the expected structure
      if (!data || !data.stats) {
        throw new Error('Estrutura de dados inválida recebida da API');
      }
      
      // Ensure all stats have default values if they're missing
      const safeStats = {
        ticketsAbertos: { value: 0, change: 0 },
        ticketsResolvidos: { value: 0, change: 0 },
        tempoMedioResposta: { value: 0, change: 0 },
        tempoMedioResolucao: { value: 0, change: 0 },
        satisfacaoCliente: { value: 0, change: 0 },
        ticketsReabertos: { value: 0, change: 0 },
        contatosPorDia: { value: 0, change: 0 },
        atendimentosPrioritarios: { value: 0, change: 0 },
        ...data.stats // Overlay with actual data, keeping defaults for any missing values
      };
      
      // Set the various data pieces
      setStats(safeStats);
      
      if (data.ticketsRecentes && Array.isArray(data.ticketsRecentes)) {
        setTicketsRecentes(data.ticketsRecentes);
      }
      
      if (data.categoriasTickets && Array.isArray(data.categoriasTickets)) {
        setCategoriasTickets(data.categoriasTickets);
      }
      
      if (data.equipeStatus && Array.isArray(data.equipeStatus)) {
        setEquipeStatus(data.equipeStatus);
      }
      
      if (data.faqPopulares && Array.isArray(data.faqPopulares)) {
        setFaqPopulares(data.faqPopulares);
      }
      
      if (data.canaisAtendimento && Array.isArray(data.canaisAtendimento)) {
        setCanaisAtendimento(data.canaisAtendimento);
      }

      if (data.trend && Array.isArray(data.trend)) {
        setTrend(data.trend);
      }
      
      toast({
        title: 'Dados carregados',
        description: 'Dados do dashboard carregados com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(error.message || 'Não foi possível carregar os dados do dashboard.');
      
      toast({
        title: 'Erro ao carregar dados',
        description: error.message || 'Não foi possível carregar os dados do dashboard.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      // Check for authentication errors and redirect if needed
      if (error.message && (
        error.message.includes('Token') || 
        error.message.includes('autenticação') || 
        error.message.includes('login') ||
        error.message.includes('sessão expirou') ||
        error.message.includes('não tem permissão')
      )) {
        // Handle auth-related redirects here if needed
        // window.location.href = '/login';
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const handleRefresh = () => {
    loadDashboardData();
    toast({
      title: 'Atualizando dados',
      description: 'Os dados do dashboard estão sendo atualizados.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  // Safely access stats properties
  const getValue = (property) => {
    return stats[property]?.value || 0;
  };
  
  const getChange = (property) => {
    return stats[property]?.change || 0;
  };

  // Show error state if there's an error
  if (error) {
    return (
      <Box w="100%" p={4}>
        <Alert 
          status="error" 
          variant="solid" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          textAlign="center" 
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Erro ao carregar dashboard
          </AlertTitle>
          <AlertDescription maxWidth="md">
            {error}
          </AlertDescription>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="white" 
            variant="outline" 
            mt={4}
            onClick={handleRefresh}
          >
            Tentar novamente
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box w="100%" p={4}>
      {/* Dashboard Header */}
      <DashboardHeader 
        timeFrame={timeFrame} 
        onTimeFrameChange={handleTimeFrameChange} 
        onRefresh={handleRefresh} 
      />

      {/* Main Stats - First Row */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Tickets Abertos" 
          value={getValue('ticketsAbertos')} 
          icon={FiInbox} 
          change={getChange('ticketsAbertos')} 
          colorScheme="blue" 
          formatter={formatNumber}
          isLoading={isLoading} 
          footerText="Aguardando atendimento"
        />
        <StatCard 
          title="Tickets Resolvidos" 
          value={getValue('ticketsResolvidos')} 
          icon={FiCheckCircle} 
          change={getChange('ticketsResolvidos')} 
          colorScheme="green" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Neste período"
        />
        <StatCard 
          title="Tempo Médio de Resposta" 
          value={formatTime(getValue('tempoMedioResposta'))} 
          icon={FiClock} 
          change={getChange('tempoMedioResposta')} 
          colorScheme="purple" 
          isLoading={isLoading}
          footerText="Primeiro contato"
        />
        <StatCard 
          title="Satisfação do Cliente" 
          value={`${getValue('satisfacaoCliente')}%`} 
          icon={FiSmile} 
          change={getChange('satisfacaoCliente')} 
          colorScheme="yellow" 
          isLoading={isLoading}
          footerText="Avaliações positivas"
        />
      </Grid>

      {/* Main Stats - Second Row */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Tempo Médio de Resolução" 
          value={formatTime(getValue('tempoMedioResolucao'))} 
          icon={MdSpeed} 
          change={getChange('tempoMedioResolucao')} 
          colorScheme="cyan" 
          isLoading={isLoading}
          footerText="Do início até o fechamento"
        />
        <StatCard 
          title="Tickets Reabertos" 
          value={getValue('ticketsReabertos')} 
          icon={FiAlertTriangle} 
          change={getChange('ticketsReabertos')} 
          colorScheme="red" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Requerem nova análise"
        />
        <StatCard 
          title="Contatos por Dia" 
          value={getValue('contatosPorDia')} 
          icon={FiMessageCircle} 
          change={getChange('contatosPorDia')} 
          colorScheme="teal" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Média diária"
        />
        <StatCard 
          title="Atendimentos Prioritários" 
          value={getValue('atendimentosPrioritarios')} 
          icon={FiAlertCircle} 
          change={getChange('atendimentosPrioritarios')} 
          colorScheme="orange" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Alta prioridade"
        />
      </Grid>

      {/* Trend Chart */}
      <Box mb={6}>
        <TrendChart data={trend} isLoading={isLoading} />
      </Box>

      {/* Tickets Recentes e Categorias */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "3fr 2fr" }} gap={6} mb={6}>
        {/* Tickets Recentes */}
        <TicketsRecentes 
          tickets={ticketsRecentes} 
          isLoading={isLoading} 
        />
        
        {/* Categorias de Tickets */}
        <CategoriasTickets 
          categorias={categoriasTickets} 
          isLoading={isLoading} 
          tempoMedioGlobal={getValue('tempoMedioResolucao')}
        />
      </Grid>

      {/* Performance da Equipe */}
      <EquipeStatus 
        equipe={equipeStatus} 
        isLoading={isLoading} 
      />

      {/* Painéis Inferiores */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "1fr 1fr" }} gap={6}>
        {/* FAQ Populares */}
        <FaqPopulares 
          faqs={faqPopulares} 
          isLoading={isLoading} 
        />
        
        {/* Canais de Atendimento */}
        <CanaisAtendimento 
          canais={canaisAtendimento} 
          isLoading={isLoading} 
        />
      </Grid>
    </Box>
  );
};

export default SuporteDashboard;